import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Calendar, Video, ExternalLink } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Toggle to re-enable action buttons: set to true to restore
  const SHOW_ACTION_BUTTONS = false;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Providers', path: '/providers' },
    { name: 'Payment Plans', path: '/sliding-fee' },
    { name: 'NP Preceptorships', path: '/preceptorship' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white shadow-lg fixed w-full top-0 z-50">
      {/* Main navbar container */}
      <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-row items-center justify-between py-3 lg:py-4">
          
          {/* Logo & Brand Section - PRIMARY VISUAL FOCUS */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center gap-4">
              {/* Logo - Large and Prominent */}
              <img 
                src="/cmc_logo.png" 
                alt="Community Medical Center of West Volusia" 
                className="h-[70px] lg:h-[80px] w-auto flex-shrink-0"
              />
              {/* Organization Name - Bold Primary Text */}
              <div className="hidden md:block flex-shrink-0">
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 leading-tight whitespace-nowrap">
                  Community Medical Center
                </h1>
                <p className="text-sm lg:text-base font-semibold text-blue-600 whitespace-nowrap">
                  of West Volusia
                </p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation Links - SECONDARY emphasis */}
          <nav className="hidden xl:flex items-center gap-1 min-w-0 flex-shrink" aria-label="Main navigation">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors rounded-md ${
                  isActive(link.path)
                    ? 'text-blue-700 bg-blue-50 font-semibold'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Patient Portal Button - Always visible on desktop */}
          <a
            href="https://mycw249.ecwcloud.com/portal30300/jsp/login.jsp"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="patient-portal-btn"
            className="hidden xl:flex items-center gap-2 bg-blue-700 text-white px-4 py-2.5 rounded-lg hover:bg-blue-800 transition-all font-bold whitespace-nowrap text-sm shadow-md flex-shrink-0"
          >
            <ExternalLink size={16} strokeWidth={2.5} className="flex-shrink-0" />
            <span>Patient Portal &amp; Bill Pay</span>
          </a>

          {/* Action Buttons - Desktop (HIGH CONTRAST) */}
          {SHOW_ACTION_BUTTONS && (
          <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
            <a
              href="https://phreesia.me/TelehealthCMCWV"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="telehealth-btn"
              className="flex items-center gap-2 bg-teal-600 text-white px-5 py-3 rounded-lg hover:bg-teal-700 transition-all shadow-md hover:shadow-lg font-bold whitespace-nowrap"
            >
              <Video size={20} strokeWidth={2.5} className="flex-shrink-0" />
              <span>Telehealth</span>
            </a>
            <a
              href="https://phreesia.me/CMCWV01"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-appointment-btn"
              className="flex items-center gap-2 bg-blue-700 text-white px-5 py-3 rounded-lg hover:bg-blue-800 transition-all shadow-md hover:shadow-lg font-bold whitespace-nowrap"
            >
              <Calendar size={20} strokeWidth={2.5} className="flex-shrink-0" />
              <span>Book Appointment</span>
            </a>
          </div>
          )}

          {/* Tablet Action Buttons (lg to xl) */}
          <a
            href="https://mycw249.ecwcloud.com/portal30300/jsp/login.jsp"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:flex xl:hidden items-center gap-1.5 bg-blue-700 text-white px-4 py-2.5 rounded-lg hover:bg-blue-800 transition-all font-bold whitespace-nowrap text-sm flex-shrink-0"
          >
            <ExternalLink size={16} strokeWidth={2.5} className="flex-shrink-0" />
            <span>Patient Portal</span>
          </a>
          {SHOW_ACTION_BUTTONS && (
          <div className="hidden lg:flex xl:hidden items-center gap-2 flex-shrink-0">
            <a
              href="https://phreesia.me/TelehealthCMCWV"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 bg-teal-600 text-white px-4 py-2.5 rounded-lg hover:bg-teal-700 transition-all font-bold whitespace-nowrap text-sm"
            >
              <Video size={18} strokeWidth={2.5} className="flex-shrink-0" />
              <span>Telehealth</span>
            </a>
            <a
              href="https://phreesia.me/CMCWV01"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 bg-blue-700 text-white px-4 py-2.5 rounded-lg hover:bg-blue-800 transition-all font-bold whitespace-nowrap text-sm"
            >
              <Calendar size={18} strokeWidth={2.5} className="flex-shrink-0" />
              <span>Book</span>
            </a>
          </div>
          )}

          {/* Mobile/Tablet menu button */}
          <div className="lg:hidden flex items-center gap-2">
            <a
              href="https://mycw249.ecwcloud.com/portal30300/jsp/login.jsp"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-1 bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap"
            >
              <ExternalLink size={14} className="flex-shrink-0" />
              <span>Portal</span>
            </a>
            {SHOW_ACTION_BUTTONS && (
            <div className="hidden sm:flex items-center gap-2">
              <a
                href="https://phreesia.me/CMCWV01"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap"
              >
                <Calendar size={14} className="flex-shrink-0" />
                <span>Book</span>
              </a>
            </div>
            )}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-gray-700 hover:text-blue-600 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="mobile-menu-btn"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t shadow-lg">
          <div className="px-4 pt-3 pb-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg text-base font-semibold transition-colors ${
                  isActive(link.path)
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {/* Mobile Action Buttons - HIGH CONTRAST */}
            <div className="pt-4 space-y-3 border-t mt-3">
              <a
                href="https://mycw249.ecwcloud.com/portal30300/jsp/login.jsp"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-patient-portal-btn"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center gap-2 bg-blue-700 text-white px-4 py-4 rounded-lg hover:bg-blue-800 w-full shadow-lg font-bold text-lg"
              >
                <ExternalLink size={22} strokeWidth={2.5} />
                <span>Patient Portal &amp; Bill Pay</span>
              </a>
            </div>
            {SHOW_ACTION_BUTTONS && (
            <div className="pt-4 space-y-3 border-t mt-3">
              <a
                href="https://phreesia.me/CMCWV01"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-appointment-btn"
                className="flex items-center justify-center gap-2 bg-blue-700 text-white px-4 py-4 rounded-lg hover:bg-blue-800 w-full shadow-lg font-bold text-lg"
              >
                <Calendar size={22} strokeWidth={2.5} />
                <span>Book an Appointment</span>
              </a>
              <a
                href="https://phreesia.me/TelehealthCMCWV"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-telehealth-btn"
                className="flex items-center justify-center gap-2 bg-teal-600 text-white px-4 py-4 rounded-lg hover:bg-teal-700 w-full shadow-lg font-bold text-lg"
              >
                <Video size={22} strokeWidth={2.5} />
                <span>Telehealth Visit</span>
              </a>
              <a
                href="tel:386-775-1792"
                className="flex items-center justify-center gap-2 bg-gray-100 text-gray-800 px-4 py-3 rounded-lg hover:bg-gray-200 w-full font-semibold"
              >
                <Phone size={20} />
                <span>Call: (386) 775-1792</span>
              </a>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky Mobile Action Bar */}
      {SHOW_ACTION_BUTTONS && (
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-2xl z-50 px-3 py-3 flex gap-2 safe-area-bottom">
        <a
          href="https://phreesia.me/CMCWV01"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sticky-book-btn"
          className="flex-1 flex items-center justify-center gap-2 bg-blue-700 text-white px-3 py-3.5 rounded-lg font-bold"
        >
          <Calendar size={20} strokeWidth={2.5} />
          <span>Book Appointment</span>
        </a>
        <a
          href="https://phreesia.me/TelehealthCMCWV"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sticky-telehealth-btn"
          className="flex-1 flex items-center justify-center gap-2 bg-teal-600 text-white px-3 py-3.5 rounded-lg font-bold"
        >
          <Video size={20} strokeWidth={2.5} />
          <span>Telehealth</span>
        </a>
      </div>
      )}
    </nav>
  );
};

export default Navbar;
